import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import {
  Badge,
  Box,
  Button,
  ContextMenu,
  ContextMenuItem,
  ContextMenuPopup,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
  Icon,
  Kbd,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsTouchDevice,
  type IconName,
} from "@salestrak/ui";

import type { NotificationRow as InboxStandardFragment } from "#app/lib/data";
import { isNotificationType, type NotificationType } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { EntityTag, EntityVisual, NeutralEntityVisual, PLATFORM_ADMIN_NAME } from "#app/components/entity";
import { DateText } from "#app/components/date-text";

/** The muted glyph that opens the preview column — keyed by the server's
 *  notification-type bucket, with a bell for a bucketless (null-action) row. */
const KIND_ICON: Record<NotificationType, IconName> = {
  assignment: "circle-user",
  mention: "at-sign",
  reminder: "alarm-clock",
  update: "square-pen",
};

/** The control that ran an inbox action, for analytics `data.via`. */
export type InboxTrigger = "click" | "hotkey" | "context_menu";

interface InboxNotificationItemProps {
  item: InboxStandardFragment;
  onClick: (item: InboxStandardFragment) => void;
  onClear: (item: InboxStandardFragment, via: InboxTrigger) => void;
  onUnclear: (item: InboxStandardFragment, via: InboxTrigger) => void;
  /** Read rows mark unread, unread rows mark read (the envelope action). */
  onToggleRead: (item: InboxStandardFragment, via: InboxTrigger) => void;
  onOpenInNewTab: (item: InboxStandardFragment) => void;
  onCopyLink: (item: InboxStandardFragment) => void;
  isCleared: boolean;
  /** Jump-to-date anchor — stamped on the first row of a day inside a month
   *  bucket (`date-group-${dayKey}`), absent everywhere else. */
  id?: string;
  /** Rows inside a bundle carry a hairline below them; the last row doesn't. */
  divider?: boolean;
}

export function InboxNotificationItem({
  item,
  onClick,
  onClear,
  onUnclear,
  onToggleRead,
  onOpenInNewTab,
  onCopyLink,
  isCleared,
  id,
  divider,
}: InboxNotificationItemProps) {
  const isTouch = useIsTouchDevice();
  const { stageMap } = useWorkspaceData();
  const author = item.author ?? item.context.author;
  const issue = item.context.issue;
  const isUnread = !item.read;
  // The leading glyph is the issue's STAGE status (a colored pie), resolved
  // from the workspace stage map — the box glyph carried no signal.
  const stage = issue?.stageId ? stageMap.get(issue.stageId) : undefined;
  const kindIcon: IconName = isNotificationType(item.type)
    ? KIND_ICON[item.type]
    : "bell";

  // When the summary opens with the actor's name ("Spencer commented on …"),
  // that leading name renders as an interactive inline chip (underline + user
  // preview) and the rest as plain text. Project events are impersonal
  // ("Project Updated: …") and never lead with a name, so they stay all-text —
  // no floating standalone name.
  const summary = item.summary ?? "";
  const authorName = author?.name?.trim() ?? "";
  const summaryNamesActor =
    !!author &&
    authorName.length > 0 &&
    summary.toLowerCase().startsWith(authorName.toLowerCase());
  const summaryRest = summaryNamesActor
    ? summary.slice(authorName.length)
    : summary;

  const handleClick = useCallback(() => {
    onClick(item);
  }, [item, onClick]);

  const handleClear = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onClear(item, "click");
    },
    [item, onClear],
  );

  const handleUnclear = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onUnclear(item, "click");
    },
    [item, onUnclear],
  );

  const handleToggleRead = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onToggleRead(item, "click");
    },
    [item, onToggleRead],
  );

  // Hover hotkeys: while the pointer is over a row, U toggles read/unread and
  // E clears/unclears — the same actions as the hover buttons. The listener is
  // bound only while hovered, and re-checks `:hover` at fire time so a missed
  // pointerleave can't leave a stale row reacting to keys (the phantom-hover
  // guard pattern). Skipped on touch, where there is no hover.
  const rowRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    if (isTouch || !hovered) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (rowRef.current && !rowRef.current.matches(":hover")) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
      ) {
        return;
      }
      if (e.code === "KeyU") {
        e.preventDefault();
        onToggleRead(item, "hotkey");
      } else if (e.code === "KeyE") {
        e.preventDefault();
        if (isCleared) onUnclear(item, "hotkey");
        else onClear(item, "hotkey");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hovered, isTouch, item, isCleared, onToggleRead, onClear, onUnclear]);

  // Right-end rest state: the calendar date. Hidden on hover (desktop) while
  // the actions take the slot. (The per-row count badge is omitted — it would
  // read "1" on every unread row until notifications bundle; unread already
  // shows through the row's weight + fill.)
  const restEnd = (
    <Box direction="row" justify="end" minWidth={58}>
      <DateText
        date={item.creationDate}
        size="xs"
        weight={isUnread ? "medium" : "normal"}
        variant={isUnread ? "default" : "faint"}
        truncate={false}
      />
    </Box>
  );

  const readLabel = isUnread ? "Mark as read" : "Mark as unread";
  // The glyph names the ACTION, not the state: an unread row offers the
  // checked envelope (mark it read), a read row the opened one (send it back
  // to unread). Both tabs carry the toggle — a cleared row still reads as
  // unread, so it still needs the switch.
  const readIcon: IconName = isUnread ? "mail-check" : "mail-open";

  const readToggle = (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            shape="square"
            aria-label={readLabel}
            onClick={handleToggleRead}
          />
        }
      >
        {/* No muted override: an interactive action reads at the button's
            default ink (muted-foreground was too faint in light mode). */}
        <Icon name={readIcon} size="sm" />
      </TooltipTrigger>
      <TooltipPopup side="top">
        <TipWithKbd label={readLabel} hotkey="U" />
      </TooltipPopup>
    </Tooltip>
  );

  const actions = (
    <>
      {readToggle}
      {isCleared ? (
        <Tooltip>
          <TooltipTrigger
            render={<Button size="sm" shape="square" onClick={handleUnclear} />}
          >
            <Icon name="repeat" size="sm" />
            Unclear
          </TooltipTrigger>
          <TooltipPopup side="top">
            <TipWithKbd label="Unclear" hotkey="E" />
          </TooltipPopup>
        </Tooltip>
      ) : (
        <Tooltip>
          <TooltipTrigger
            render={<Button size="sm" shape="square" onClick={handleClear} />}
          >
            <Icon name="check" size="sm" />
            Clear
          </TooltipTrigger>
          <TooltipPopup side="top">
            <TipWithKbd label="Clear" hotkey="E" />
          </TooltipPopup>
        </Tooltip>
      )}
    </>
  );

  // The row is clickable but is NOT a button element — it hosts real buttons
  // (clear/toggle-read), and a button cannot nest inside a button. It is a
  // group named by what it says, so a screen reader can tell rows apart.
  const rowLabel = [issue?.name ?? "Unknown Project", summary].filter(Boolean).join(": ");
  const row = (
    <Box
      ref={rowRef}
      id={id}
      role="group"
      aria-label={rowLabel}
      direction="row"
      align="center"
      height={44}
      paddingLeft="md"
      paddingRight="base"
      group="row"
      // Every row sits on the bundle surface; the INK carries read state
      // (body + medium unread, faint + regular read). A second signal on the
      // surface is noise: a recessed read row sank into a darker band in dark
      // mode and dimmed the whole item, and a lifted one restated what the
      // faint rung already says.
      hoverBackground
      cursor="pointer"
      border={divider ? "bottom" : undefined}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onClick={handleClick}
    >
      {/* Title column — fixed 30%, never shrinks, truncates */}
      <Box
        direction="row"
        align="center"
        width="30%"
        shrink={false}
        minWidth={0}
        paddingRight="lg"
      >
        {issue ? (
          <Box direction="row" align="center" gap="sm" minWidth={0}>
            {/* Stage-status pie + its name in a tooltip. */}
            <Tooltip>
              <TooltipTrigger
                render={<Box direction="row" align="center" shrink={false} />}
              >
                <EntityVisual
                  entity={{
                    kind: "stage",
                    id: issue.stageId ?? "no-stage",
                    stage: stage ?? {},
                  }}
                  // xs pie (14px) reads level with the line glyphs; xs and sm
                  // share the same footprint box, so the name rail is unmoved.
                  size="xs"
                />
              </TooltipTrigger>
              <TooltipPopup side="top">{stage?.name ?? "No stage"}</TooltipPopup>
            </Tooltip>
            <EntityTag
              entity={{ kind: "issue", id: issue.issueId, issue }}
              display="label"
              weight={isUnread ? "medium" : "regular"}
              labelInk={isUnread ? undefined : "faint"}
              // The row owns the press; the name keeps its hover card.
              preview
              interactive={false}
            />
          </Box>
        ) : (
          // Non-entity fallback: NeutralEntityVisual shares the issue chip's
          // footprint box, so the glyph lands on the same rail as a real
          // issue's — a bare <Icon> drifts a few px off it.
          <Box direction="row" align="center" gap="sm" minWidth={0}>
            <NeutralEntityVisual icon="home" size="sm" />
            {/* Same read/unread ink as the issue chip so a titled and an
                Unknown row don't disagree on weight/rung. */}
            <Text
              size="sm"
              weight={isUnread ? "medium" : "normal"}
              variant={isUnread ? "default" : "faint"}
              truncate
            >
              Unknown Project
            </Text>
          </Box>
        )}
      </Box>

      {/* Preview column — kind glyph + summary; fills, truncates. When the
          summary leads with the actor, that name is an interactive inline chip
          (underline + preview) sitting in the sentence; otherwise it's plain. */}
      <Box direction="row" align="center" gap="sm" flex={1} minWidth={0}>
        <Icon name={kindIcon} size="sm" color={isUnread ? "muted" : "faint"} />
        {item.onBehalf ? (
          <Badge color="secondary" size="sm">
            <Icon name="shield" size="xs" />
            {PLATFORM_ADMIN_NAME}
          </Badge>
        ) : null}
        <Text
          size="sm"
          weight={isUnread ? "medium" : "normal"}
          variant={isUnread ? "default" : "faint"}
          truncate
        >
          {summaryNamesActor && author ? (
            <>
              <EntityTag
                entity={{ kind: "user", id: author.userId, user: author }}
                display="label"
                as="text"
                size="sm"
                weight={isUnread ? "medium" : "regular"}
                labelInk={isUnread ? undefined : "faint"}
                underlineHover
              />
              {summaryRest}
            </>
          ) : (
            summary
          )}
        </Text>
      </Box>

      {/* Right end: unread marker + date, swapped for the actions on hover.
          The swap is pure CSS — no JS hover state. Touch keeps the rest state
          and reaches the same actions through the context menu below. */}
      <Box direction="row" align="center" gap="sm" shrink={false}>
        {isTouch ? (
          restEnd
        ) : (
          <>
            <Box
              direction="row"
              align="center"
              gap="sm"
              className="group-hover/row:hidden"
            >
              {restEnd}
            </Box>
            <Box
              hidden direction="row"
              align="center"
              gap="xs"
              className="group-hover/row:flex"
            >
              {actions}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );

  // The row carries a right-click context menu on every viewport (long-press on
  // touch). The clear/read actions mirror the hover buttons + their hotkeys;
  // the task actions need an issue, so they only show when the row has one.
  return (
    <ContextMenu>
      <ContextMenuTrigger render={row} />
      {/* Same chrome as the filter popover: the 224px action rung + an 8px
          gutter so items read as inset floating pills. */}
      <ContextMenuPopup width="action">
        {isCleared ? (
          <ContextMenuItem onClick={() => onUnclear(item, "context_menu")}>
            <Icon name="repeat" size="md" color="muted" />
            Unclear
            <ContextMenuShortcut>E</ContextMenuShortcut>
          </ContextMenuItem>
        ) : (
          <ContextMenuItem onClick={() => onClear(item, "context_menu")}>
            <Icon name="check" size="md" color="muted" />
            Clear
            <ContextMenuShortcut>E</ContextMenuShortcut>
          </ContextMenuItem>
        )}
        <ContextMenuItem onClick={() => onToggleRead(item, "context_menu")}>
          <Icon name={readIcon} size="md" color="muted" />
          {readLabel}
          <ContextMenuShortcut>U</ContextMenuShortcut>
        </ContextMenuItem>
        {issue && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={() => onClick(item)}>
              <Icon name="arrow-up-right" size="md" color="muted" />
              Open
            </ContextMenuItem>
            <ContextMenuItem onClick={() => onOpenInNewTab(item)}>
              <Icon name="external-link" size="md" color="muted" />
              Open in new tab
            </ContextMenuItem>
            <ContextMenuItem onClick={() => onCopyLink(item)}>
              <Icon name="link" size="md" color="muted" />
              Copy link
            </ContextMenuItem>
          </>
        )}
      </ContextMenuPopup>
    </ContextMenu>
  );
}

/** Tooltip content: an action label with its hover-hotkey chip trailing it. */
function TipWithKbd({ label, hotkey }: { label: string; hotkey: string }) {
  return (
    <Box direction="row" align="center" gap="sm">
      {label}
      <Kbd>{hotkey}</Kbd>
    </Box>
  );
}
